/**
 * Funções HTTP do CRUD de escritório + gestão de membros (E2.F4). // ux-neutrality:allow doc-comment
 *
 * Cinco endpoints sob /api/escritorios: // ux-neutrality:allow doc-comment
 *   GET /me, PATCH /me, GET /me/membros, POST /me/membros, DELETE /me/membros/:id.
 *
 * Erros HTTP/rede são classificados em `EscritoriosError.reason` (estável,
 * consumido pela UI para escolher mensagem em pt-BR).
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { UsuarioRole } from '@/lib/auth';

export interface EscritorioRecord {
  id: string;
  nome: string;
  cnpj: string | null;
  owner_id: string;
  perito_titular: string | null;
  assistente_revisora: string | null;
  logo_blob_path: string | null;
  created_at: string;
  deleted_at: string | null;
}

export interface EscritorioUpdatePayload {
  nome?: string;
  cnpj?: string;
}

export interface MemberRecord {
  id: string;
  email: string;
  role: UsuarioRole;
  is_active: boolean;
  created_at: string;
  deleted_at: string | null;
}

export interface MemberCreatePayload {
  email: string;
  password: string;
}

export interface MemberRoleUpdatePayload {
  /** `certificador` só é aceito pelo servidor na sessão do escritório-plataforma (E52.F1). */
  role: 'member' | 'revisor' | 'certificador';
}

export type EscritoriosErrorReason =
  | 'validation'
  | 'network'
  | 'conflict'
  | 'not_found'
  | 'unauthorized'
  | 'owner_required'
  | 'owner_cannot_be_removed'
  | 'escritorio_update_empty'
  | 'user_has_no_escritorio'
  | 'unknown';

export class EscritoriosError extends Error {
  public readonly reason: EscritoriosErrorReason;

  constructor(reason: EscritoriosErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'EscritoriosError';
  }
}

function _detail(err: AxiosError): string | null {
  const data = err.response?.data as { detail?: unknown } | undefined;
  if (data !== undefined && typeof data.detail === 'string') {
    return data.detail;
  }
  return null;
}

const _DETAIL_TO_REASON: Record<string, EscritoriosErrorReason> = {
  OWNER_REQUIRED: 'owner_required',
  OWNER_CANNOT_BE_REMOVED: 'owner_cannot_be_removed',
  ESCRITORIO_UPDATE_EMPTY: 'escritorio_update_empty',
  USER_HAS_NO_ESCRITORIO: 'user_has_no_escritorio',
};

const _STATUS_TO_REASON: Record<number, EscritoriosErrorReason> = {
  401: 'unauthorized',
  403: 'unauthorized',
  404: 'not_found',
  409: 'conflict',
  422: 'validation',
};

function _reasonFor(axiosErr: AxiosError): EscritoriosErrorReason {
  const status = axiosErr.response?.status;
  const detail = _detail(axiosErr);
  if (detail !== null) {
    const detailReason = _DETAIL_TO_REASON[detail];
    if (detailReason !== undefined) return detailReason;
  }
  if (status !== undefined) {
    const statusReason = _STATUS_TO_REASON[status];
    if (statusReason !== undefined) return statusReason;
  }
  if (axiosErr.code === 'ERR_NETWORK' || axiosErr.code === 'ECONNABORTED') {
    return 'network';
  }
  return 'unknown';
}

function _classify(err: unknown): EscritoriosError {
  if (!axios.isAxiosError(err)) {
    return new EscritoriosError('unknown');
  }
  return new EscritoriosError(_reasonFor(err as AxiosError));
}

export async function fetchMyEscritorio(client: AxiosInstance): Promise<EscritorioRecord> {
  try {
    const { data } = await client.get<EscritorioRecord>('/escritorios/me');
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

export async function patchMyEscritorio(
  client: AxiosInstance,
  payload: EscritorioUpdatePayload,
): Promise<EscritorioRecord> {
  try {
    const { data } = await client.patch<EscritorioRecord>('/escritorios/me', payload);
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

export async function listMyMembers(client: AxiosInstance): Promise<MemberRecord[]> {
  try {
    const { data } = await client.get<MemberRecord[]>('/escritorios/me/membros');
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

export async function createMyMember(
  client: AxiosInstance,
  payload: MemberCreatePayload,
): Promise<MemberRecord> {
  try {
    const { data } = await client.post<MemberRecord>('/escritorios/me/membros', payload);
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

export async function updateMyMember(
  client: AxiosInstance,
  memberId: string,
  payload: MemberRoleUpdatePayload,
): Promise<MemberRecord> {
  try {
    const { data } = await client.patch<MemberRecord>(
      `/escritorios/me/membros/${memberId}`,
      payload,
    );
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

export async function deleteMyMember(client: AxiosInstance, memberId: string): Promise<void> {
  try {
    await client.delete(`/escritorios/me/membros/${memberId}`);
  } catch (err) {
    throw _classify(err);
  }
}
